import { ListFilter, Check } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/** `null` means "no filter" (every value passes) -- distinct from an empty
 *  Set, which means every row is filtered out. Callers should treat the two
 *  the same way Excel's own "(Select All)" does: fully checked vs. fully
 *  unchecked. */
export type ColumnFilterValue = Set<string> | null;

interface TableColumnFilterProps {
  label: string;
  values: string[];
  selected: ColumnFilterValue;
  onChange: (next: ColumnFilterValue) => void;
  blankLabel?: string;
}

export default function TableColumnFilter({
  label,
  values,
  selected,
  onChange,
  blankLabel = "(Blank)",
}: TableColumnFilterProps) {
  const counts = new Map<string, number>();
  for (const raw of values) {
    const value = raw ?? "";
    counts.set(value, (counts.get(value) ?? 0) + 1);
  }

  const options = Array.from(counts.keys()).sort((a, b) =>
    (a || blankLabel).localeCompare(b || blankLabel),
  );

  const isFiltered = selected !== null;
  const checkedValues = selected ?? new Set(options);

  const toggle = (value: string) => {
    const next = new Set(checkedValues);
    if (next.has(value)) next.delete(value);
    else next.add(value);
    onChange(next.size === options.length ? null : next);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={`Filter by ${label}`}
          className={cn(
            "inline-flex h-5 w-5 shrink-0 items-center justify-center rounded hover:bg-muted",
            isFiltered ? "text-primary" : "text-muted-foreground",
          )}
        >
          <ListFilter className="h-3.5 w-3.5" aria-hidden />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-0" align="start">
        <Command>
          <CommandInput placeholder={`Search ${label.toLowerCase()}…`} />
          <div className="flex items-center justify-between border-b border-border px-2 py-1.5 text-xs">
            <button
              type="button"
              className="text-primary hover:underline"
              onClick={() => onChange(null)}
            >
              Select all
            </button>
            <button
              type="button"
              className="text-muted-foreground hover:underline"
              onClick={() => onChange(new Set())}
            >
              Clear
            </button>
          </div>
          <CommandList>
            <CommandEmpty>No matches.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const checked = checkedValues.has(option);
                return (
                  <CommandItem
                    key={option || "__blank__"}
                    value={option || blankLabel}
                    onSelect={() => toggle(option)}
                  >
                    <div
                      className={cn(
                        "mr-2 flex h-4 w-4 shrink-0 items-center justify-center rounded-sm border border-primary",
                        checked ? "bg-primary text-primary-foreground" : "opacity-50",
                      )}
                    >
                      {checked && <Check className="h-3 w-3" aria-hidden />}
                    </div>
                    <span className="flex-1 truncate">{option || blankLabel}</span>
                    <span className="ml-2 text-xs text-muted-foreground">
                      {counts.get(option)}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function passesColumnFilter(filter: ColumnFilterValue, value: string): boolean {
  return filter === null || filter.has(value);
}
